import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Skeleton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {translations} from '../../../../../../../i18n/translations';
import {
  SECTORS_IDS,
  SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING,
} from '../../../../../../../utils/constants/hardcoded';
import {
  ActivitiesSectorAndGroups,
  FetchActivitiesSectorsAndGroups,
} from '../shared/use-fetch-activities-sectors-and-groups';
import {ExpandMore} from '@mui/icons-material';
import {ActivitiesGroup} from '../../../../../../../backend/interfaces/activities-group';

type CalendarSideActivitiesSectorsFilterProps = {
  activitiesSectorsAndGroupsData: FetchActivitiesSectorsAndGroups;
  onChange: (filters: string[]) => void;
};

export function CalendarSideActivitiesSectorsFilter(
  props: CalendarSideActivitiesSectorsFilterProps
) {
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});
  const [toggleAll, setToggleAll] = useState<boolean>(true);
  const [toggleBirthday, setToggleBirthday] = useState<boolean>(true);
  const {t} = useTranslation();
  const {hasReadPermission} = useAuth();

  const loading = props.activitiesSectorsAndGroupsData.isSectorsLoading;
  // Filter activities sectors according to user permissions
  const filterItems = useMemo(() => {
    const items = props.activitiesSectorsAndGroupsData.items?.filter((x) =>
      hasReadPermission(SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING[x.sector.id!])
    );

    return items;
  }, [props.activitiesSectorsAndGroupsData.items, hasReadPermission]);

  const textSx: SxProps = {
    fontSize: '14px',
    fontWeight: 500,
    color: 'gray.main',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
  };

  useEffect(() => {
    const checkboxes = filterItems?.reduce(
      (acc, sectorsAndGroups) => {
        acc[sectorsAndGroups.sector.id!] = true;

        sectorsAndGroups.groups?.forEach((group) => {
          acc[group.id!] = true;
        });
        return acc;
      },
      {} as Record<string, boolean>
    );

    if (checkboxes) checkboxes[BIRTHDAY_SECTOR_ID] = true;

    setCheckboxes(checkboxes ?? {});
  }, [filterItems]);

  const fireOnChangeEvent = (newCheckboxes: Record<string, boolean>, checkToggleAll = true) => {
    const selectedCheckboxes = Object.entries(newCheckboxes)
      ?.filter(([key, value]) => {
        return !SECTORS_IDS.includes(key) && !value;
      })
      .map(([key]) => key);

    props.onChange(selectedCheckboxes);

    if (checkToggleAll) {
      if (selectedCheckboxes.length === 0) {
        // All sectors are selected
        setToggleAll(true);
      } else {
        // Not all sectors are selected
        setToggleAll(false);
      }
    }
  };

  return (
    <Stack flexDirection="column" p="8px">
      {loading ? (
        new Array(6).fill(1).map((_, index) => (
          <Stack key={`skel_sector_filters_${index}`} sx={{flexDirection: 'row', gap: '6px'}}>
            <Skeleton width="20px" height="30px" />
            <Skeleton width="60%" height="30px" />
          </Stack>
        ))
      ) : (
        <>
          <Stack sx={{flexDirection: 'row', alignItems: 'center'}}>
            <Checkbox
              size="small"
              checked={toggleAll}
              onChange={(evt, checked) => {
                const newCheckboxes = toogleAllBoxes(checked, filterItems);
                newCheckboxes[BIRTHDAY_SECTOR_ID] = checked;
                // Update state
                setCheckboxes(newCheckboxes);
                setToggleAll(checked);
                setToggleBirthday(checked);
                // Fire
                fireOnChangeEvent(newCheckboxes, false);
              }}
            />
            <Typography sx={textSx}>{t(translations.planner.side.see_all)}</Typography>
          </Stack>

          {filterItems?.map((sectorAndGroups) => (
            <Accordion
              key={`side_sector_${sectorAndGroups.sector.id}`}
              sx={{
                '&.MuiAccordion-root': {
                  boxShadow: 'none',
                  margin: 0,
                },
                '&.MuiAccordion-root::before': {
                  content: 'none',
                },
                '& .MuiAccordionSummary-root': {
                  padding: 0,
                  minHeight: '38px',
                  width: '100%',
                },
                '& .MuiAccordionSummary-root.Mui-expanded': {
                  minHeight: '38px',
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <Stack sx={{flexDirection: 'row', alignItems: 'center'}}>
                <Checkbox
                  size="small"
                  checked={checkboxes[sectorAndGroups.sector.id!] ?? true}
                  onChange={(evt, checked) => {
                    const newCheckboxes = toogleASectorBox(checked, sectorAndGroups, checkboxes);
                    newCheckboxes[BIRTHDAY_SECTOR_ID] = toggleBirthday;
                    // Update state
                    setCheckboxes(newCheckboxes);
                    // Fire
                    fireOnChangeEvent(newCheckboxes);
                  }}
                />
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={textSx}>{sectorAndGroups.sector.name}</Typography>
                </AccordionSummary>
              </Stack>

              {sectorAndGroups.groups?.map((group) => (
                <AccordionDetails
                  key={`side_group_${group.id}`}
                  sx={{
                    '&.MuiAccordionDetails-root': {
                      padding: 0,
                      paddingLeft: '7px',
                    },
                  }}
                >
                  <Stack sx={{flexDirection: 'row', alignItems: 'center'}}>
                    <Checkbox
                      size="small"
                      checked={checkboxes[group.id] ?? true}
                      onChange={(evt, checked) => {
                        const newCheckboxes = toogleAGroupBox(
                          checked,
                          sectorAndGroups,
                          group,
                          checkboxes
                        );
                        newCheckboxes[BIRTHDAY_SECTOR_ID] = toggleBirthday;
                        // Update state
                        setCheckboxes(newCheckboxes);
                        // Fire
                        fireOnChangeEvent(newCheckboxes);
                      }}
                    />
                    <Typography sx={textSx}>{group.name}</Typography>
                  </Stack>
                </AccordionDetails>
              ))}
            </Accordion>
          ))}

          <Stack sx={{flexDirection: 'row', alignItems: 'center'}}>
            <Checkbox
              size="small"
              checked={toggleBirthday}
              onChange={(evt, checked) => {
                const newCheckboxes = {...checkboxes, [BIRTHDAY_SECTOR_ID]: checked};
                // Update state
                setToggleBirthday(checked);
                // Fire
                fireOnChangeEvent(newCheckboxes);
              }}
            />
            <Typography sx={textSx}>{t(translations.planner.side.birthdays)}</Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------------------

export const BIRTHDAY_SECTOR_ID = 'birthdays';

function toogleAllBoxes(checked: boolean, items?: ActivitiesSectorAndGroups[]) {
  return (
    items?.reduce(
      (acc, sectorAndGroups) => {
        acc[sectorAndGroups.sector.id!] = checked;

        sectorAndGroups.groups?.forEach((group) => {
          acc[group.id!] = checked;
        });
        return acc;
      },
      {} as Record<string, boolean>
    ) ?? {}
  );
}

function toogleASectorBox(
  checked: boolean,
  sectorAndGroups: ActivitiesSectorAndGroups,
  checkboxes?: Record<string, boolean>
) {
  return {
    ...checkboxes,
    [sectorAndGroups.sector.id!]: checked,
    ...sectorAndGroups.groups?.reduce(
      (acc, group) => {
        acc[group.id!] = checked;
        return acc;
      },
      {} as Record<string, boolean>
    ),
  };
}

function toogleAGroupBox(
  checked: boolean,
  sectorAndGroups: ActivitiesSectorAndGroups,
  group: ActivitiesGroup,
  checkboxes?: Record<string, boolean>
) {
  return {
    ...checkboxes,
    [sectorAndGroups.sector.id!]: sectorAndGroups.groups?.every((g) =>
      // If all groups are checked, then the sector is checked
      g.id === group.id ? checked : checkboxes?.[g.id]
    )
      ? true
      : false,
    [group.id!]: checked,
  };
}
